// @ts-nocheck -- copied verbatim from the application being replaced.
//
// Quarantined, not exempted. This file has not been rewritten to this project's
// settings and does not pass them; checking it would report errors nobody is
// going to fix in place, and loosening the settings to accommodate it would
// apply that to code written here too. Vite still compiles it -- esbuild strips
// types without checking them.
//
// Delete this line when the file is rewritten. It is the marker for what is left
import { logger } from '@/shared/observability/logger';
import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { toast } from '../components/ui/toast';
import { CollectionService, AddItemToCollectionRequest } from '../services/collection.service';
import { BrandAssetService } from '../services/brand-asset.service';

// ============================================================================
// TYPES
// ============================================================================

export interface CollectionItem {
  id: string;
  collectionId?: string;
  brandId: string;
  searchId: string;
  domain: string;
  brandName: string;
  brandWebsite?: string;
  favicon?: string;
  status: 'completed' | 'processing' | 'failed';
  completeness: number;
  addedAt: string;
  notes?: string;
  logos: {
    primary: boolean;
    secondary: boolean;
    icon: boolean;
  };
  colors: {
    detected: boolean;
    count: number;
  };
  fonts: {
    detected: boolean;
    count: number;
  };
  metadata: {
    detected: boolean;
  };
  guidelines: {
    detected: boolean;
  };
}

interface CollectionsContextType {
  collections: CollectionItem[];
  addToCollection: (item: Omit<CollectionItem, 'addedAt' | 'id'>) => Promise<void>;
  removeFromCollection: (brandId: string) => Promise<void>;
  isInCollection: (brandId: string) => boolean;
  clearCollections: () => Promise<void>;
  totalCollections: number;
  refreshCollections: () => Promise<void>;
  isLoading: boolean;
  _ensureLoaded: () => Promise<void>;
}

// ============================================================================
// CONTEXT
// ============================================================================

const CollectionsContext = createContext<CollectionsContextType | undefined>(undefined);

// ============================================================================
// PROVIDER - NOW API-INTEGRATED
// ============================================================================

export function CollectionsProvider({ children }: { children: ReactNode }) {
  const [collections, setCollections] = useState<CollectionItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [defaultCollectionId, setDefaultCollectionId] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  /**
   * Load all items from the "All Saved" collection
   */
  const loadCollections = useCallback(async () => {
    // Prevent duplicate loads
    if (isLoading || isInitialized) {
      return;
    }

    try {
      setIsLoading(true);
      
      // Get or create default "All Saved" collection
      const defaultCollection:any = await CollectionService.getOrCreateDefaultCollection();
      
      // Validate we got a valid collection with an ID
      if (!defaultCollection || !defaultCollection.collectionId || defaultCollection.collectionId === 'undefined') {
        logger.error('[CollectionsContext] Failed to get valid default collection:', defaultCollection);
        setCollections([]);
        setIsInitialized(true);
        return;
      }
      
      setDefaultCollectionId(defaultCollection.collectionId);
      
      // Get items from default collection
      // getCollectionItems answers { success, data }, not the array itself; reading it as an array left this
      // empty for ever, so no brand ever looked saved.
      const response = await CollectionService.getCollectionItems(defaultCollection.collectionId);
      setCollections(Array.isArray(response?.data) ? response.data : []);
      setIsInitialized(true);
    } catch (error) {
      logger.error('[CollectionsContext] Failed to load collections:', error);
      setCollections([]); // Ensure state is always an array
      setIsInitialized(true);
      // Don't show error toast on initial load to avoid spamming user
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, isInitialized]);

  /**
   * Add item to the default "All Saved" collection
   */
  const addToCollection = async (item: Omit<CollectionItem, 'addedAt' | 'id'>) => {
    try {
      // Ensure collections are loaded if not already
      if (!isInitialized) {
        await loadCollections();
      }

      // Check if already exists
      if (Array.isArray(collections) && collections.some((c) => c.brandId === item.brandId)) {
        toast.info('Already in collections', {
          description: `${item.brandName} is already saved in your collections.`,
        });
        return;
      }

      // Get default collection ID
      let collectionId = defaultCollectionId;
      
      if (!collectionId || collectionId === 'undefined' || collectionId === 'null') {
        const defaultCollection = await CollectionService.getOrCreateDefaultCollection();
        
        if (!defaultCollection || !defaultCollection.id || defaultCollection.id === 'undefined') {
          throw new Error('Failed to get or create default collection - invalid ID returned');
        }
        
        collectionId = defaultCollection.id;
        setDefaultCollectionId(collectionId);
      }

      // Final validation before API call
      if (!collectionId || collectionId === 'undefined' || collectionId === 'null') {
        throw new Error('Collection ID is undefined or invalid');
      }

      // Prepare item data for API (only brandId and notes as per API spec)
      const itemData: AddItemToCollectionRequest = {
        brandId: parseInt(item.brandId, 10), // Convert string to number
        notes: undefined, // Optional field, can be added later if needed
      };

      // Add to API
      const newItem = await CollectionService.addItemToCollection(collectionId, itemData);

      // Update local state
      setCollections((prev) => Array.isArray(prev) ? [newItem, ...prev] : [newItem]);

      toast.success('Added to collections', {
        description: `${item.brandName} has been saved to your collections.`,
      });

      // Refresh brands dashboard to update UI
      await BrandAssetService.getDashboardBrands({
        status: 'all',
        page: 0,
        limit: 5
      });

      // Dispatch event to notify components
      window.dispatchEvent(new CustomEvent('collection-updated'));
    } catch (error: any) {
      logger.error('[CollectionsContext] Failed to add to collection:', error);
      // toast.error('Failed to add to collections', {
      //   description: error.message || 'An error occurred while adding the item.',
      // });
    }
  };

  /**
   * Remove item from the default "All Saved" collection
   */
  const removeFromCollection = async (brandId: string) => {
    try {
      // Get default collection ID
      let collectionId = defaultCollectionId;
      
      if (!collectionId || collectionId === 'undefined' || collectionId === 'null') {
        const defaultCollection = await CollectionService.getOrCreateDefaultCollection();
        
        if (!defaultCollection || !defaultCollection.id || defaultCollection.id === 'undefined') {
          throw new Error('Failed to get or create default collection - invalid ID returned');
        }
        
        collectionId = defaultCollection.id;
        setDefaultCollectionId(collectionId);
      }

      // Final validation before API call
      if (!collectionId || collectionId === 'undefined' || collectionId === 'null') {
        throw new Error('Collection ID is undefined or invalid');
      }

      // Find item in local state
      const item = Array.isArray(collections) ? collections.find((c) => c.brandId === brandId) : undefined;

      // Remove from API
      await CollectionService.removeItemFromCollection(collectionId, brandId);

      // Update local state
      setCollections((prev) => Array.isArray(prev) ? prev.filter((c) => c.brandId !== brandId) : []);

      if (item) {
        toast.success('Removed from collections', {
          description: `${item.brandName} has been removed from your collections.`,
        });
      }
    } catch (error: any) {
      logger.error('[CollectionsContext] Failed to remove from collection:', error);
      toast.error('Failed to remove from collections', {
        description: error.message || 'An error occurred while removing the item.',
      });
    }
  };

  /**
   * Check if item is in collections (by brandId)
   */
  const isInCollection = (brandId: string) => {
    // Defensive check to ensure collections is an array
    if (!Array.isArray(collections)) {
      logger.warn('[CollectionsContext] collections is not an array:', collections);
      return false;
    }
    return collections.some((c) => c.brandId === brandId);
  };

  /**
   * Clear all collections (not implemented - would require deleting default collection)
   */
  const clearCollections = async () => {
    toast.info('Clear collections not available', {
      description: 'To remove items, please delete them individually or delete custom collections.',
    });
  };

  /**
   * Refresh collections from API
   */
  const refreshCollections = async () => {
    await loadCollections();
  };

  return (
    <CollectionsContext.Provider
      value={{
        collections,
        addToCollection,
        removeFromCollection,
        isInCollection,
        clearCollections,
        totalCollections: Array.isArray(collections) ? collections.length : 0,
        refreshCollections,
        isLoading,
        _ensureLoaded: loadCollections,
      }}
    >
      {children}
    </CollectionsContext.Provider>
  );
}

// ============================================================================
// HOOK
// ============================================================================

export function useCollections() {
  const context = useContext(CollectionsContext);

  if (context === undefined) {
    throw new Error('useCollections must be used within a CollectionsProvider');
  }

  return context;
}